import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CheckCircle2, Edit, Plus, Search, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { partsNeededAPI } from '@shared/apiClient';
import PartsNeededFormModal from '../components/PartsNeededFormModal';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

export default function PartsNeeded() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterReceived, setFilterReceived] = useState('open');
  const [page, setPage] = useState(1);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedPart, setSelectedPart] = useState(null);

  const queryParams = useMemo(() => ({
    page,
    limit: 25,
    ...(searchTerm && { search: searchTerm }),
    ...(filterReceived === 'open' && { received: false }),
    ...(filterReceived === 'received' && { received: true })
  }), [page, searchTerm, filterReceived]);

  const { data, isLoading } = useQuery(
    ['partsNeeded', queryParams],
    () => partsNeededAPI.getPartsNeeded(queryParams),
    { keepPreviousData: true }
  );

  const deleteMutation = useMutation(
    (id) => partsNeededAPI.deletePartNeeded(id),
    {
      onSuccess: () => {
        toast.success('Part request deleted');
        queryClient.invalidateQueries('partsNeeded');
        queryClient.invalidateQueries('partsNeededSummary');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete part request');
      }
    }
  );

  const parts = data?.data?.parts || [];
  const pagination = data?.data?.pagination;

  const handleOpenCreate = () => {
    setFormMode('create');
    setSelectedPart(null);
    setIsFormModalOpen(true);
  };

  const handleOpenEdit = (part) => {
    setFormMode('edit');
    setSelectedPart(part);
    setIsFormModalOpen(true);
  };

  const handleDelete = (part) => {
    if (window.confirm(`Delete request for "${part.name}"?`)) {
      deleteMutation.mutate(part.id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Parts Needed</h1>
          <p className="mt-1 text-sm text-gray-500">
            Request and track parts needed during dispatch and field work ({pagination?.total || 0} requests)
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Request Part
        </button>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-6 space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search parts..."
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setPage(1);
                }}
                className="input pl-10"
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Filter:</span>
              <button
                onClick={() => {
                  setFilterReceived('open');
                  setPage(1);
                }}
                className={`px-3 py-1 rounded-md ${
                  filterReceived === 'open'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Open
              </button>
              <button
                onClick={() => {
                  setFilterReceived('received');
                  setPage(1);
                }}
                className={`px-3 py-1 rounded-md ${
                  filterReceived === 'received'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Received
              </button>
              <button
                onClick={() => {
                  setFilterReceived('all');
                  setPage(1);
                }}
                className={`px-3 py-1 rounded-md ${
                  filterReceived === 'all'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="text-center py-10 text-gray-500">Loading parts needed...</div>
        ) : parts.length === 0 ? (
          <div className="text-center py-10 text-gray-500">No part requests found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Part
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requested
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Received
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {parts.map((part) => (
                  <tr key={part.id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        {part.received && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                        {part.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {part.quantity}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {formatDateTime(part.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {formatDateTime(part.receivedAt)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => handleOpenEdit(part)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(part)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && (
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing {pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={pagination.page <= 1}
                className="btn btn-secondary btn-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="btn btn-secondary btn-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <PartsNeededFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        mode={formMode}
        part={selectedPart}
      />
    </div>
  );
}
